import "regenerator-runtime/runtime"; // fontkit's Indic shaper needs this global
import { flattenStampText, type StampZone } from "./flattenStamp";
import { PDFDocument, PDFArray, type PDFPage, degrees, StandardFonts, rgb, PDFTextField, PDFCheckBox, PDFDropdown, PDFRadioGroup, PDFSignature, type PDFField } from "pdf-lib";
import { readOutline, mapOutline, mapOutlineSubset, writeOutline, type MergedOutlineNode } from "./pdfBookmarks";
import { carryFormFields, renameFieldsOnPage } from "./pdfFormCopy";
import { stampOutputMetadata, stemOfPdfName } from "./pdfMetadata";
import { planFilePages, hasPageEdits, normalizeTurn, type PageEdits, type ArrangedPage } from "./mergePagePlan";
import { pageView, storedRectFromViewed } from "./pageView";
import { refreshAppearances, appearancesAreFinal, dropDeadAnnotations } from "./formAppearance";
import fontkit from "@pdf-lib/fontkit";
import { allowsDevanagari } from "./ocrText";
import { looksUnreadable } from "./ocrQuality";
import { rotateImageBlob } from "./ocrImage";
import JSZip from "jszip";
import * as XLSX from "xlsx";
import { uniqueFolderNames, stemForImages, imageFileName, uniquePages } from "./jpgNaming";
import { loadPdfDocument, openPdfBytes, renderPageToJpegBlob, renderPageToImageBlob, pdfBytesToBlob, extractPdfTextLines, extractPdfTextLinesWithPosition } from "./utils";
import { browserLadderEnv, type LevelCheckpoint } from "./targetCompression";
import { compressPdfBytes, type CompressLimit, type CompressMode, type CompressResult } from "./compressLadder";
import type { ImageCodec } from "./imageRecompress";
import { diffLines, MAX_DIFF_LINES, type DiffLine } from "@/lib/textDiff";
import { loadImageElement } from "@/tools/image/utils";

/** Returns a PDF's page count. Small enough that it might look like it's
 * not worth extracting, but "how many pages does this PDF have" is
 * needed by several tools independently of any other operation (Split,
 * Rotate, and others all load a file just to answer this one question
 * before doing anything else) -- worth a shared function specifically
 * so that need doesn't quietly turn back into copy-pasted PDFDocument.load()
 * calls across tools as more of them adopt this module. */
export async function getPdfPageCount(file: File): Promise<number> {
  const pdf = await PDFDocument.load(await file.arrayBuffer());
  return pdf.getPageCount();
}

/**
 * Pure, framework-agnostic PDF operations -- extracted from each tool's
 * React component so the same core logic is callable from more than one
 * place. Each function takes plain inputs (File objects, primitives) and
 * returns plain outputs (Uint8Array bytes) -- no React state, no toast
 * calls, no analytics tracking. That's deliberate: a function tangled up
 * with UI concerns can only ever be used inside its one component. A
 * pure function can be called from:
 *   - the existing tool page (wrapping it with loading state / toasts,
 *     same as before)
 *   - a future composite "master PDF tool" that chains several of these
 *     together without duplicating any of the underlying logic
 *   - a future server-side API endpoint, with little or no rewriting,
 *     since there's no browser-only UI code to strip out first
 *
 * Error handling policy: these throw real Error objects with messages
 * meant to be shown to a user (not stack traces) -- callers catch and
 * route to a toast, an API error response, or a test assertion,
 * whichever fits that caller. Nothing here decides how an error is
 * presented, only what went wrong.
 */

/** Combines multiple PDF files into one, in the given order. Requires
 * at least 2 files -- callers should validate that before calling, but
 * this also guards it directly since a 1-file or 0-file call is a
 * genuine misuse, not just a UI-level nicety to skip. */
export type PdfLoadStatus =
  | { status: "ok"; doc: PDFDocument; pageCount: number; hasFormFields: boolean }
  | { status: "encrypted" }
  | { status: "invalid" };

/**
 * Validates a single file as a real, readable PDF without assuming
 * success -- used by Merge PDF (and meant to be reused by any other
 * multi-file PDF tool later) so a bad file is identified by name
 * *before* an operation runs, not discovered only when the whole
 * operation throws a single generic error with no file attribution.
 *
 * Loads with ignoreEncryption so an encrypted file's structure can
 * still be parsed well enough to tell encrypted apart from genuinely
 * corrupt -- pdf-lib's own isEncrypted flag is the real signal for
 * the former; any load that still throws even in this lenient mode
 * means the file is damaged beyond that, not just locked.
 */
export async function loadPdfSafely(file: File): Promise<PdfLoadStatus> {
  let bytes: ArrayBuffer;
  try {
    bytes = await file.arrayBuffer();
  } catch {
    return { status: "invalid" };
  }
  try {
    const pdf = await PDFDocument.load(bytes, { ignoreEncryption: true, updateMetadata: false });
    if (pdf.isEncrypted) return { status: "encrypted" };
    // Fillable form fields can't be carried into a merged file (pdf-lib has
    // no way to copy an AcroForm) -- reported up front so a caller can say
    // so before the merge instead of the fields silently going missing.
    let hasFormFields = false;
    try {
      hasFormFields = pdf.getForm().getFields().length > 0;
    } catch {
      /* a malformed form dictionary shouldn't make the file itself unusable */
    }
    return { status: "ok", doc: pdf, pageCount: pdf.getPageCount(), hasFormFields };
  } catch {
    return { status: "invalid" };
  }
}

export interface MergeOptions {
  /** Pages to take from each file ("1-3,5"); blank or missing means every page. Same order as `files`. */
  ranges?: (string | undefined)[];
  /** Add a blank page after any file (except the last) that contributes an odd number of pages, so each file starts on a front side when printed double-sided. */
  padOdd?: boolean;
  /** Pages turned off and quarter turns chosen in the page panel, per file (same order as `files`). Applied after the range. */
  pageEdits?: (PageEdits | undefined)[];
  /**
   * An exact page-by-page result (from the page arranger, which can mix pages from different files in any order). When given,
   * it replaces ranges, pageEdits and padOdd: the output is exactly these pages. Each file still gets a top-level bookmark
   * (pointing at its first page in the result) when any source has bookmarks; its inner bookmarks are not carried over.
   */
  arrangement?: ArrangedPage[];
}

export async function mergePdfs(
  files: File[],
  onProgress?: (current: number, total: number) => void,
  options: MergeOptions = {},
): Promise<Uint8Array> {
  if (files.length < 2) {
    throw new Error("Merging requires at least 2 PDF files");
  }
  if (options.arrangement) return mergeArranged(files, options.arrangement, onProgress);
  const mergedPdf = await PDFDocument.create();
  let firstTitle: string | undefined;
  let firstAuthor: string | undefined;
  // One top-level bookmark per source file, holding that file's own
  // bookmarks remapped onto the copied pages -- only written if at least one
  // source actually had bookmarks, so merging plain files adds nothing.
  const outline: MergedOutlineNode[] = [];
  let anyBookmarks = false;
  for (let i = 0; i < files.length; i++) {
    const ab = await files[i].arrayBuffer();
    let pdf: PDFDocument;
    try {
      pdf = await PDFDocument.load(ab);
    } catch (err) {
      // Carries the actual file name -- the generic "Failed to merge
      // PDFs" toast this used to produce gave no way to tell which of
      // several files was the problem. Callers that already validate
      // files up front (Merge PDF's own UI) won't normally hit this,
      // but any other caller of mergePdfs gets a real, attributable
      // error instead of losing that information here.
      const reason = err instanceof Error && /encrypt/i.test(err.message) ? "is password-protected" : "couldn't be read (it may be damaged)";
      throw new Error(`"${files[i].name}" ${reason}`);
    }
    if (i === 0) {
      firstTitle = pdf.getTitle() || undefined;
      firstAuthor = pdf.getAuthor() || undefined;
    }
    let indices = pdf.getPageIndices();
    let turns: number[] = [];
    const wanted = options.ranges?.[i]?.trim();
    const edits = options.pageEdits?.[i];
    const edited = hasPageEdits(edits);
    if (wanted || edited) {
      const plan = planFilePages(pdf.getPageCount(), wanted, edits);
      if ("error" in plan) throw new Error(`"${files[i].name}": ${plan.error}`);
      indices = plan.pages.map((p) => p.index);
      turns = plan.pages.map((p) => p.rotate);
    }
    const pages = await mergedPdf.copyPages(pdf, indices);
    pages.forEach((page, k) => {
      if (turns[k]) page.setRotation(degrees((page.getRotation().angle + turns[k]) % 360));
      mergedPdf.addPage(page);
    });
    if (options.padOdd && i < files.length - 1 && pages.length % 2 === 1) {
      const last = pages[pages.length - 1].getSize();
      mergedPdf.addPage([last.width, last.height]);
    }
    const sourceOutline = readOutline(pdf);
    if (sourceOutline.length > 0) anyBookmarks = true;
    const children = wanted || edited ? mapOutlineSubset(sourceOutline, new Map(indices.map((si, k) => [si, pages[k]] as const))) : mapOutline(sourceOutline, pages);
    outline.push({ title: files[i].name.replace(/\.pdf$/i, ""), page: pages[0] ?? null, children });
    onProgress?.(i + 1, files.length);
  }
  if (anyBookmarks) writeOutline(mergedPdf, outline);
  // Title/Author carry over from the FIRST file when they're real values; a placeholder
  // ("untitled", "anonymous", "Microsoft Word - ...") or nothing falls back to the output's own
  // name (QA retest 6, MP-14). Producer/Creator are FreeDailyPro. See pdfMetadata.ts.
  stampOutputMetadata(mergedPdf, { title: firstTitle, author: firstAuthor, fallbackTitle: `${stemOfPdfName(files[0].name)}-merged` });
  return mergedPdf.save();
}

async function mergeArranged(files: File[], arrangement: ArrangedPage[], onProgress?: (current: number, total: number) => void): Promise<Uint8Array> {
  if (arrangement.length === 0) throw new Error("No pages left to merge");
  const mergedPdf = await PDFDocument.create();
  const sources: PDFDocument[] = [];
  for (let i = 0; i < files.length; i++) {
    try {
      sources.push(await PDFDocument.load(await files[i].arrayBuffer()));
    } catch (err) {
      const reason = err instanceof Error && /encrypt/i.test(err.message) ? "is password-protected" : "couldn't be read (it may be damaged)";
      throw new Error(`"${files[i].name}" ${reason}`);
    }
  }
  const firstPages = new Map<number, ReturnType<PDFDocument["getPages"]>[number]>();
  for (let k = 0; k < arrangement.length; k++) {
    const a = arrangement[k];
    const src = sources[a.fileIndex];
    if (!src || a.index < 0 || a.index >= src.getPageCount()) throw new Error("The page arrangement no longer matches the files. Open the arranger again.");
    const [page] = await mergedPdf.copyPages(src, [a.index]);
    const turn = normalizeTurn(a.rotate);
    if (turn) page.setRotation(degrees((page.getRotation().angle + turn) % 360));
    mergedPdf.addPage(page);
    if (!firstPages.has(a.fileIndex)) firstPages.set(a.fileIndex, page);
    onProgress?.(k + 1, arrangement.length);
  }
  if (sources.some((s) => readOutline(s).length > 0)) {
    const outline: MergedOutlineNode[] = [];
    files.forEach((f, i) => { const page = firstPages.get(i); if (page) outline.push({ title: f.name.replace(/\.pdf$/i, ""), page, children: [] }); });
    writeOutline(mergedPdf, outline);
  }
  stampOutputMetadata(mergedPdf, { title: sources[0].getTitle() || undefined, author: sources[0].getAuthor() || undefined, fallbackTitle: `${stemOfPdfName(files[0].name)}-merged` });
  return mergedPdf.save();
}

export type SplitRangeResult = { pages: number[] } | { error: string };

/** Parses one range like "3", "1-3", or "5 - 8" against a real page
 * count, returning either the resolved 0-indexed page list or a
 * user-facing error message -- kept alongside splitPdf itself since
 * parsing page ranges is specific to this one operation, not a general
 * utility other tools would need. */
export function parseSplitRange(range: string, pageCount: number): SplitRangeResult {
  // Phones and word processors type en and em dashes and minus signs where a hyphen was meant.
  const cleaned = range.replace(/\s+/g, "").replace(/[\u2010-\u2015\u2212]/g, "-");
  if (!cleaned) return { error: "Empty range" };
  const pagesLabel = `${pageCount} ${pageCount === 1 ? "page" : "pages"}`;

  if (cleaned.includes("-")) {
    const parts = cleaned.split("-");
    if (parts.length !== 2) return { error: `"${range}" isn't a valid range` };
    // "5-" means "page 5 to the last page".
    if (parts[1] === "" && parts[0] !== "") parts[1] = String(pageCount);
    const [s, e] = parts.map(Number);
    if (parts.some((x) => x === "") || !Number.isInteger(s) || !Number.isInteger(e)) return { error: `"${range}" isn't a valid range` };
    if (s < 1 || e > pageCount) return { error: `"${range}" is outside this PDF's ${pagesLabel}` };
    if (s > e) return { error: `"${range}" starts after it ends` };
    return { pages: Array.from({ length: e - s + 1 }, (_, i) => s + i - 1) };
  }

  const n = Number(cleaned);
  if (!Number.isInteger(n)) return { error: `"${range}" isn't a valid page number` };
  if (n < 1 || n > pageCount) return { error: `Page ${n} is outside this PDF's ${pagesLabel}` };
  return { pages: [n - 1] };
}

/** Copies the given 0-indexed pages of `src` into `dst`, in order, and brings the document-level
 * things pdf-lib's copyPages leaves behind: the bookmarks that point at kept pages and the form
 * definition for the fields that are on them. */
export async function copyPagesWithStructure(src: PDFDocument, dst: PDFDocument, indices: number[]): Promise<{ formFields: number; bookmarks: number }> {
  const copied = await dst.copyPages(src, indices);
  copied.forEach((p) => dst.addPage(p));
  const pageFor = new Map<number, (typeof copied)[number]>();
  indices.forEach((srcIndex, i) => {
    if (!pageFor.has(srcIndex)) pageFor.set(srcIndex, copied[i]);
  });
  const outline = mapOutlineSubset(readOutline(src), pageFor);
  writeOutline(dst, outline);
  const formFields = carryFormFields(src, dst);
  const countAll = (nodes: MergedOutlineNode[]): number => nodes.reduce((n, x) => n + 1 + countAll(x.children), 0);
  return { formFields, bookmarks: countAll(outline) };
}

/** "1-3", "5", or "1-3_7" -- the pages of one group, as used in file names. */
export function pageGroupLabel(pages: number[]): string {
  const nums = pages.map((p) => p + 1);
  const runs: string[] = [];
  let i = 0;
  while (i < nums.length) {
    let j = i;
    while (j + 1 < nums.length && nums[j + 1] === nums[j] + 1) j++;
    runs.push(j > i ? `${nums[i]}-${nums[j]}` : `${nums[i]}`);
    i = j + 1;
  }
  return runs.join("_");
}

export interface SplitPart {
  blob: Uint8Array;
  name: string;
  /** What is in this part, e.g. "pages 1-3". */
  rangeLabel: string;
  pageCount: number;
  formFields: number;
  bookmarks: number;
}

/** Splits a PDF into one file per range, or combines the given ranges
 * into a single reordered PDF if combineIntoOne is true. Ranges are
 * 0-indexed page-number arrays, already resolved by parseSplitRange --
 * this function doesn't parse range strings itself, keeping "parse user
 * input" and "do the actual split" as separate, independently testable
 * steps. File names say which pages they hold ("report-pages-1-3.pdf"),
 * so two splits never overwrite each other as split_1.pdf. */
export async function splitPdf(
  file: File,
  pageGroups: number[][],
  combineIntoOne: boolean,
  onProgress?: (current: number, total: number) => void
): Promise<SplitPart[]> {
  const pdf = await PDFDocument.load(await file.arrayBuffer());
  const stem = stemOfPdfName(file.name);

  if (combineIntoOne) {
    const allPages = pageGroups.flat();
    const newPdf = await PDFDocument.create();
    const kept = await copyPagesWithStructure(pdf, newPdf, allPages);
    stampOutputMetadata(newPdf, { title: pdf.getTitle(), author: pdf.getAuthor(), fallbackTitle: `${stem}-combined` });
    const bytes = await newPdf.save();
    return [{ blob: bytes, name: `${stem}-combined.pdf`, rangeLabel: `pages ${pageGroups.map(pageGroupLabel).join(", ")}`, pageCount: allPages.length, ...kept }];
  }

  const output: SplitPart[] = [];
  const usedNames = new Set<string>();
  for (let i = 0; i < pageGroups.length; i++) {
    const group = pageGroups[i];
    const newPdf = await PDFDocument.create();
    const kept = await copyPagesWithStructure(pdf, newPdf, group);
    const label = pageGroupLabel(group);
    stampOutputMetadata(newPdf, { title: pdf.getTitle(), author: pdf.getAuthor(), fallbackTitle: `${stem} (part ${i + 1})` });
    const bytes = await newPdf.save();
    // The same range listed twice would otherwise produce two files with one name.
    let name = `${stem}-${group.length === 1 ? "page" : "pages"}-${label}.pdf`;
    for (let n = 2; usedNames.has(name); n++) name = `${stem}-${group.length === 1 ? "page" : "pages"}-${label} (${n}).pdf`;
    usedNames.add(name);
    output.push({ blob: bytes, name, rangeLabel: `${group.length === 1 ? "page" : "pages"} ${label.replace(/_/g, ", ")}`, pageCount: group.length, ...kept });
    onProgress?.(i + 1, pageGroups.length);
  }
  return output;
}

export type { CompressMode, CompressLimit, CompressUnchangedReason, CompressResult } from "./compressLadder";

/** Reduces a PDF's file size. The ladder (what each mode does, level by level) is in compressLadder.ts, shared with the API;
 *  this is the browser's entry point to it.
 *  - auto / medium / strong / target: the older presets (tidy, then pictures inside, optionally pages as pictures).
 *  - balanced / aggressive / extreme / size: the four modes on one ladder of levels.
 * Every result keeps the page sizes, the title, bookmarks, links and form fields (the same document is
 * saved), except the pages-as-pictures levels, which say so. */
export async function compressPdf(
  file: File,
  mode: CompressMode,
  options: {
    targetBytes?: number;
    allowPictures?: boolean;
    /** For "size": the furthest step the search may take. Default "extreme". */
    limit?: CompressLimit;
    onProgress?: (message: string) => void;
    shouldStop?: () => boolean;
    codec?: ImageCodec;
    /** Aggressive / Extreme: keep going through the heavier levels even when the file is already about 98% smaller. */
    runAllLevels?: boolean;
    /** Called as each level finishes, with a complete file, so the person can see every step and stop at any of them. */
    onLevel?: (checkpoint: LevelCheckpoint) => void;
  } = {}
): Promise<CompressResult> {
  const original = new Uint8Array(await file.arrayBuffer());
  const { codec, ...rest } = options;
  return compressPdfBytes(original, mode, browserLadderEnv(file, codec), { ...rest, fileName: file.name });
}

/** The size, in points, of the smallest page that holds a w x h page turned clockwise by `degrees`,
 * and where the turned page's lower-left corner must be placed so that it fits exactly. */
export function rotatedBounds(w: number, h: number, degreesClockwise: number): { width: number; height: number; offsetX: number; offsetY: number } {
  const rad = (-degreesClockwise * Math.PI) / 180; // pdf-lib turns counter-clockwise
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const xs = [0, w, w, 0].map((x, i) => x * cos - [0, 0, h, h][i] * sin);
  const ys = [0, w, w, 0].map((x, i) => x * sin + [0, 0, h, h][i] * cos);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  return { width: maxX - minX, height: maxY - minY, offsetX: -minX, offsetY: -minY };
}

/** Rotates PDF pages. `rotations` maps 1-indexed page number to a clockwise angle in degrees.
 *
 * The document itself is edited and saved, never rebuilt, so title, bookmarks, links and form
 * fields all survive:
 *  - a multiple of 90 only changes the page's rotation flag (text stays selectable);
 *  - any other angle redraws that page, still as vector content (no picture): the page's own
 *    content is placed, turned, on a new page sized to hold it, at its original resolution and with
 *    selectable text. Links and form fields ON such a page are not carried over. */
export async function rotatePdf(
  file: File,
  rotations: Record<number, number>,
  onProgress?: (message: string) => void,
  shouldStop?: () => boolean
): Promise<Uint8Array> {
  const doc = await PDFDocument.load(await file.arrayBuffer(), { updateMetadata: false });
  const pageCount = doc.getPageCount();

  for (let i = 0; i < pageCount; i++) {
    if (shouldStop?.()) throw new Error("Rotation stopped");
    const pageNumber = i + 1;
    const angle = rotations[pageNumber] || 0;
    if (angle === 0) continue;
    const page = doc.getPage(i);
    const stored = page.getRotation().angle;
    const quarterTurn = Math.round(angle / 90) * 90;
    const residual = angle - quarterTurn;

    if (Math.abs(residual) < 1e-9) {
      onProgress?.(`Page ${pageNumber} of ${pageCount}...`);
      page.setRotation(degrees((((stored + quarterTurn) % 360) + 360) % 360));
      continue;
    }

    onProgress?.(`Turning page ${pageNumber} of ${pageCount} by ${Math.round(angle * 100) / 100}°...`);
    const { width: w, height: h } = page.getSize(); // the unrotated box the content is drawn in
    const total = stored + angle; // how far the raw content must turn clockwise to look as the reader showed it, turned
    const box = rotatedBounds(w, h, total);
    const embedded = await doc.embedPage(page);
    const replacement = doc.insertPage(i, [box.width, box.height]);
    replacement.drawPage(embedded, { x: box.offsetX, y: box.offsetY, rotate: degrees(-total) });
    doc.removePage(i + 1);
  }

  stampOutputMetadata(doc, { title: doc.getTitle(), author: doc.getAuthor(), fallbackTitle: stemOfPdfName(file.name) });
  return doc.save({ useObjectStreams: true });
}

export type PageNumberPosition = "bottom-center" | "bottom-right" | "bottom-left" | "top-center" | "top-right" | "top-left";

/**
 * Adds page numbers to every page of a PDF, extracted from
 * AddPageNumbers.tsx. startAt lets the numbering continue from a
 * number other than 1 -- useful when this PDF will be bound together
 * with other documents later (a set of appendices continuing the main
 * document's numbering, for instance).
 */
export type PageNumberFormat = "plain" | "page-of-total" | "prefixed";

export type PageNumberStyle = "arabic" | "roman" | "ROMAN" | "alpha" | "ALPHA";

/** 1 -> "1" | "i" | "I" | "a" | "A"; 27 -> "aa" in the alpha styles, 4 -> "iv" in roman. */
export function formatPageNumber(n: number, style: PageNumberStyle = "arabic"): string {
  if (style === "arabic" || n < 1) return String(n);
  if (style === "roman" || style === "ROMAN") {
    const map: [number, string][] = [[1000, "m"], [900, "cm"], [500, "d"], [400, "cd"], [100, "c"], [90, "xc"], [50, "l"], [40, "xl"], [10, "x"], [9, "ix"], [5, "v"], [4, "iv"], [1, "i"]];
    let rest = n, out = "";
    for (const [v, sym] of map) while (rest >= v) { out += sym; rest -= v; }
    return style === "ROMAN" ? out.toUpperCase() : out;
  }
  let out = "", rest = n;
  while (rest > 0) { rest--; out = String.fromCharCode(97 + (rest % 26)) + out; rest = Math.floor(rest / 26); }
  return style === "ALPHA" ? out.toUpperCase() : out;
}

export interface AddPageNumbersOptions {
  style?: PageNumberStyle;
  /** The "of Y" in "Page 3 of Y"; defaults to the last number used. */
  totalOverride?: number;
  margin?: number;
  color?: { r: number; g: number; b: number };
  format?: PageNumberFormat;
  prefix?: string; // used when format is "prefixed", e.g. "Page " -> "Page 1"
  skipFirstPage?: boolean;
  fontSize?: number;
  /** 1-indexed page range to number -- pages outside this range are
   * left untouched. Takes priority over skipFirstPage when both are
   * set (skipFirstPage is really just a common-case shortcut for "range
   * starting at page 2"). */
  pageRange?: string;
}

/** A typo in the page range -- the user's input is wrong, not the PDF. Callers show it inline, never as a "corrupted PDF" error. */
export class PageRangeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PageRangeError";
  }
}

export async function addPageNumbers(
  file: File,
  position: PageNumberPosition,
  startAt: number,
  options: AddPageNumbersOptions = {}
): Promise<Blob> {
  const { format = "plain", prefix = "", skipFirstPage = false, fontSize = 10, style = "arabic", margin = 28, color = { r: 0.35, g: 0.35, b: 0.35 } } = options;
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdf = await PDFDocument.load(bytes);
  const { drawTextAnywhere, measureTextAnywhere } = await import("./drawTextAnywhere");
  const pages = pdf.getPages();
  const MARGIN = margin;

  // A range takes priority when given; skipFirstPage is the common-case
  // shortcut for "every page except the first" (a title/cover page) --
  // resolved to an explicit index set either way, so the per-page loop
  // below has one simple check rather than two different conditions to
  // juggle.
  let numberedIndices: Set<number>;
  if (options.pageRange) {
    const parsed = parseMultiRange(options.pageRange, pages.length);
    if ("error" in parsed) throw new PageRangeError(parsed.error); // never number silently with a bad range (PN-2)
    numberedIndices = new Set(parsed.pages);
  } else {
    numberedIndices = new Set(pages.map((_, i) => i).filter(i => !(skipFirstPage && i === 0)));
  }

  // Numbering only counts pages that actually get a number -- if page 1
  // is skipped, page 2 is still labeled "1" (the first numbered page),
  // not "2", matching how a book with an unnumbered title page works.
  let displayNumber = startAt;
  const totalNumbered = numberedIndices.size;

  for (let i = 0; i < pages.length; i++) {
    const page = pages[i];
    if (!numberedIndices.has(i)) continue;

    const rawLabel = formatPageNumber(displayNumber, style);
    const label =
      format === "page-of-total" ? `Page ${rawLabel} of ${options.totalOverride !== undefined ? formatPageNumber(options.totalOverride, style) : formatPageNumber(startAt + totalNumbered - 1, style)}` :
      format === "prefixed" ? `${prefix}${rawLabel}` :
      rawLabel;
    displayNumber++;

    const view = pageView(page);
    const { width, height } = view;
    const textWidth = await measureTextAnywhere(pdf, label, fontSize);

    const isTop = position.startsWith("top");
    const vy = isTop ? height - MARGIN : MARGIN;

    let vx: number;
    if (position.endsWith("center")) vx = width / 2 - textWidth / 2;
    else if (position.endsWith("right")) vx = width - MARGIN - textWidth;
    else vx = MARGIN;

    const { x, y } = view.toStored(vx, vy);
    await drawTextAnywhere(pdf, page, label, { x, y, size: fontSize, color, rotateDegrees: view.rotation });
  }

  const outBytes = await pdf.save();
  return pdfBytesToBlob(outBytes);
}

export interface CropBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * Scans a rendered page image for the bounding box of its actual
 * (non-white) content, returning a fractional CropBox that trims the
 * surrounding whitespace -- the "auto-trim" feature the crop UI offers
 * as a starting point instead of requiring a fully manual drag every
 * time. Treats any pixel darker than a near-white threshold as
 * content; scans from each edge inward until it finds the first row/
 * column with real content, rather than a more expensive full-image
 * scan. A small padding fraction is added back around the detected
 * bounds so the result doesn't crop flush against the very edge of
 * real content, which tends to look cramped.
 */
export function detectContentBounds(imageData: ImageData, paddingFraction = 0.01): CropBox {
  const { data, width, height } = imageData;
  const WHITE_THRESHOLD = 250; // near-white, tolerant of light scan noise/JPEG artifacts

  const isRowBlank = (y: number) => {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      if (data[i] < WHITE_THRESHOLD || data[i + 1] < WHITE_THRESHOLD || data[i + 2] < WHITE_THRESHOLD) return false;
    }
    return true;
  };
  const isColBlank = (x: number) => {
    for (let y = 0; y < height; y++) {
      const i = (y * width + x) * 4;
      if (data[i] < WHITE_THRESHOLD || data[i + 1] < WHITE_THRESHOLD || data[i + 2] < WHITE_THRESHOLD) return false;
    }
    return true;
  };

  let top = 0;
  while (top < height && isRowBlank(top)) top++;
  let bottom = height - 1;
  while (bottom > top && isRowBlank(bottom)) bottom--;
  let left = 0;
  while (left < width && isColBlank(left)) left++;
  let right = width - 1;
  while (right > left && isColBlank(right)) right--;

  // Entirely blank page -- nothing to trim to, fall back to the full
  // page rather than returning a zero-size or inverted box.
  if (top >= bottom || left >= right) return { x: 0, y: 0, w: 1, h: 1 };

  const padX = width * paddingFraction;
  const padY = height * paddingFraction;
  const x = Math.max(0, (left - padX) / width);
  const y = Math.max(0, (top - padY) / height);
  const w = Math.min(1 - x, (right - left + padX * 2) / width);
  const h = Math.min(1 - y, (bottom - top + padY * 2) / height);
  return { x, y, w, h };
}

/**
 * Crops every page of a PDF to a region given as fractions (0-1) of
 * each page's own width/height, extracted from CropPdf.tsx. The
 * fractional selection is relative to the page's top-left (matching an
 * image-preview convention), while PDF page coordinates use a
 * bottom-left origin -- the y-axis flip below accounts for that
 * difference.
 */
export async function cropPdf(file: File, box: CropBox, pageIndices?: number[]): Promise<Blob> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdf = await PDFDocument.load(bytes);
  const allPages = pdf.getPages();
  // Defaults to every page (the previous, only behavior) when no scope
  // is given -- pageIndices lets a caller crop just the current page,
  // or an explicit range, instead of always applying to the whole
  // document.
  const targetIndices = pageIndices ?? allPages.map((_, i) => i);
  const targetSet = new Set(targetIndices);

  allPages.forEach((page, i) => {
    if (!targetSet.has(i)) return;
    // The box was drawn on the page as the reader sees it (turned, and already cropped if it was):
    // convert it to the stored page's own coordinates (see pageView.ts).
    const view = pageView(page);
    const vw = box.w * view.width;
    const vh = box.h * view.height;
    const r = storedRectFromViewed(view, box.x * view.width, view.height - box.y * view.height - vh, vw, vh);
    page.setCropBox(r.x, r.y, r.width, r.height);
  });
  const out = await pdf.save();
  return pdfBytesToBlob(out);
}

/**
 * Resizes every page of a PDF to a uniform target page size, extracted
 * as a general-purpose version of the same "contain, don't crop"
 * scaling addNormalizedPage in bookAssembly.ts uses for mixed-source
 * book assembly. Each source page is embedded as a vector object and
 * scaled to fit within the target dimensions (aspect ratio preserved,
 * centered) -- text and vector content stay text and vector, nothing
 * gets rasterized just to resize it.
 */
export async function resizePdfPages(file: File, targetWidth: number, targetHeight: number): Promise<Blob> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const srcPdf = await PDFDocument.load(bytes);
  const outPdf = await PDFDocument.create();
  const pageCount = srcPdf.getPageCount();

  for (let i = 0; i < pageCount; i++) {
    const sourcePage = srcPdf.getPage(i);
    const embedded = await outPdf.embedPage(sourcePage);
    const scale = Math.min(targetWidth / embedded.width, targetHeight / embedded.height);
    const drawW = embedded.width * scale;
    const drawH = embedded.height * scale;
    const page = outPdf.addPage([targetWidth, targetHeight]);
    page.drawPage(embedded, { x: (targetWidth - drawW) / 2, y: (targetHeight - drawH) / 2, width: drawW, height: drawH });
  }

  const out = await outPdf.save();
  return pdfBytesToBlob(out);
}

export const PDF_PAGE_SIZES_PT: Record<"a4" | "letter" | "legal" | "a5", { width: number; height: number; label: string }> = {
  a4: { width: 595.28, height: 841.89, label: "A4" },
  letter: { width: 612, height: 792, label: "US Letter" },
  legal: { width: 612, height: 1008, label: "US Legal" },
  a5: { width: 419.53, height: 595.28, label: "A5" },
};

export type PdfPageSizeId = keyof typeof PDF_PAGE_SIZES_PT;

/**
 * Stamps a diagonal, semi-transparent text watermark across every page
 * of a PDF, extracted from PdfWatermark.tsx.
 */
/** Defaults for a text watermark: mid-grey at 40% reads clearly on white paper yet leaves the text underneath legible. */
export const DEFAULT_WATERMARK_COLOR = { r: 0.5, g: 0.5, b: 0.5 };
export const DEFAULT_WATERMARK_OPACITY = 0.4;

export type WatermarkPosition = "center" | "top-left" | "top-right" | "bottom-left" | "bottom-right" | "tiled";



interface WatermarkOptions {
  opacity?: number; // 0-1
  position?: WatermarkPosition;
  fontSize?: number; // text watermark only
  color?: { r: number; g: number; b: number }; // text watermark only, 0-1 each
  rotationDegrees?: number;
  /** 0-based pages to stamp; every page when omitted. */
  pages?: number[];
  /** "above" (default) draws over the page; "behind" draws under the page's own content, so text stays fully dark. */
  layer?: "above" | "behind";
}

/**
 * Moves what was just drawn on this page (pdf-lib keeps it in one content stream of its own, added after the
 * page's own content) to the front of the page's /Contents, so the page's text and pictures paint over it.
 * A page that paints an opaque background or a full-page scan will hide a watermark placed behind it.
 */
export function sendDrawnContentBehind(page: PDFPage): void {
  const ref = (page as unknown as { contentStreamRef?: unknown }).contentStreamRef;
  if (!ref) return;
  const contents = page.node.Contents();
  if (!(contents instanceof PDFArray)) return;
  for (let i = 0; i < contents.size(); i++) {
    if (String(contents.get(i)) === String(ref)) {
      contents.remove(i);
      contents.insert(0, ref as never);
      return;
    }
  }
}

/**
 * Where a single (non-tiled) watermark starts (its bottom-left, before turning), so that the TURNED content
 * sits at the named spot: centred really is centred, corners keep a margin. All in viewed coordinates.
 */
function getWatermarkOrigin(
  position: WatermarkPosition,
  pageWidth: number,
  pageHeight: number,
  contentWidth: number,
  contentHeight: number,
  angleDegrees = 0
): { x: number; y: number } {
  const margin = 40;
  const t = (angleDegrees * Math.PI) / 180;
  const cos = Math.cos(t), sin = Math.sin(t);
  const bw = Math.abs(contentWidth * cos) + Math.abs(contentHeight * sin);
  const bh = Math.abs(contentWidth * sin) + Math.abs(contentHeight * cos);
  const cx = position.endsWith("left") ? margin + bw / 2 : position.endsWith("right") ? pageWidth - margin - bw / 2 : pageWidth / 2;
  const cy = position.startsWith("top") ? pageHeight - margin - bh / 2 : position.startsWith("bottom") ? margin + bh / 2 : pageHeight / 2;
  return { x: cx - (contentWidth / 2) * cos + (contentHeight / 2) * sin, y: cy - (contentWidth / 2) * sin - (contentHeight / 2) * cos };
}

export async function addTextWatermark(file: File, rawWatermarkText: string, options: WatermarkOptions = {}): Promise<Blob> {
  const watermarkText = rawWatermarkText.replace(/[\r\n\t]+/g, " ").trim();
  const { opacity = DEFAULT_WATERMARK_OPACITY, position = "center", fontSize = 50, color = DEFAULT_WATERMARK_COLOR, rotationDegrees = 45 } = options;
  const pdf = await PDFDocument.load(await file.arrayBuffer());
  const { drawTextAnywhere, measureTextAnywhere } = await import("./drawTextAnywhere");
  if (!watermarkText) throw new Error("Enter the watermark text");
  // Long text must still fit the page: shrink the type until the turned text sits inside the smallest page.
  let size = fontSize;
  const wanted = options.pages ? new Set(options.pages) : null;
  const pagesList = pdf.getPages().filter((_, i) => !wanted || wanted.has(i));
  if (pagesList.length === 0) throw new Error("No pages to watermark");
  if (position !== "tiled") {
    const t = (rotationDegrees * Math.PI) / 180;
    let w0 = await measureTextAnywhere(pdf, watermarkText, size, true);
    const fitW = Math.min(...pagesList.map((p) => pageView(p).width)) - 80;
    const fitH = Math.min(...pagesList.map((p) => pageView(p).height)) - 80;
    const bw = Math.abs(w0 * Math.cos(t)) + Math.abs(size * 0.75 * Math.sin(t));
    const bh = Math.abs(w0 * Math.sin(t)) + Math.abs(size * 0.75 * Math.cos(t));
    const k = Math.min(1, fitW / bw, fitH / bh);
    if (k < 1) size = Math.max(6, size * k);
  }
  const textWidth = await measureTextAnywhere(pdf, watermarkText, size, true);
  const textHeight = size * 0.75;

  for (const page of pagesList) {
    const view = pageView(page);
    const { width, height } = view; // the page as the reader sees it, whatever its /Rotate or crop box
    const drawOne = (vx: number, vy: number) => {
      const { x, y } = view.toStored(vx, vy);
      return drawTextAnywhere(pdf, page, watermarkText, { x, y, size, bold: true, color, opacity, rotateDegrees: rotationDegrees + view.rotation });
    };

    if (position === "tiled") {
      // A real repeating pattern across the page, not just one stamp --
      // useful for a document likely to be cropped or partially
      // photographed, where a single corner/center watermark could be
      // cut out of the visible portion entirely.
      const stepX = textWidth + 80;
      const stepY = textHeight + 60;
      for (let y = -stepY; y < height + stepY; y += stepY) {
        for (let x = -stepX; x < width + stepX; x += stepX) {
          await drawOne(x, y);
        }
      }
    } else {
      const { x, y } = getWatermarkOrigin(position, width, height, textWidth, textHeight, rotationDegrees);
      await drawOne(x, y);
    }
    if (options.layer === "behind") sendDrawnContentBehind(page);
  }
  const bytes = await pdf.save();
  return pdfBytesToBlob(bytes);
}

/**
 * Stamps an image (a logo, for example) onto every page -- the real
 * feature the tool's own catalog listing already claimed ("text or
 * image watermarks") before this existed; found and fixed as a real,
 * confirmed mismatch between marketing and what actually shipped.
 * Supports PNG and JPEG, the two formats pdf-lib can embed directly
 * without needing a separate conversion step.
 */
export async function addImageWatermark(file: File, imageFile: File, options: WatermarkOptions & { widthFraction?: number } = {}): Promise<Blob> {
  const { opacity = 0.4, position = "center", rotationDegrees = 0 } = options;
  const widthFraction = options.widthFraction ?? (position === "tiled" ? 0.18 : 0.3);
  const pdf = await PDFDocument.load(await file.arrayBuffer());
  const { normalizeImageFile } = await import("./imagePdf");
  imageFile = await normalizeImageFile(imageFile); // upright, and PNG/JPEG whatever was chosen
  const imageBytes = await imageFile.arrayBuffer();
  const isPng = imageFile.type === "image/png";
  const embeddedImage = isPng ? await pdf.embedPng(imageBytes) : await pdf.embedJpg(imageBytes);
  const wanted = options.pages ? new Set(options.pages) : null;

  for (const [pageIdx, page] of Array.from(pdf.getPages().entries())) {
    if (wanted && !wanted.has(pageIdx)) continue;
    const view = pageView(page);
    const { width, height } = view;
    // Sized from the page, not the picture's pixels, and kept inside the page whatever its shape.
    let drawWidth = width * widthFraction;
    let drawHeight = (drawWidth * embeddedImage.height) / embeddedImage.width;
    const maxH = (position === "tiled" ? 0.3 : 0.6) * height;
    if (drawHeight > maxH) { drawHeight = maxH; drawWidth = (drawHeight * embeddedImage.width) / embeddedImage.height; }
    const draw = (vx: number, vy: number) => {
      const { x, y } = view.toStored(vx, vy);
      page.drawImage(embeddedImage, { x, y, width: drawWidth, height: drawHeight, opacity, rotate: degrees(rotationDegrees + view.rotation) });
    };
    if (position === "tiled") {
      const stepX = drawWidth + 70, stepY = drawHeight + 70;
      let row = 0;
      for (let y = 20; y < height; y += stepY, row++) for (let x = 20 + (row % 2) * (stepX / 2); x < width; x += stepX) draw(x, y);
    } else {
      const o = getWatermarkOrigin(position, width, height, drawWidth, drawHeight, rotationDegrees);
      draw(o.x, o.y);
    }
    if (options.layer === "behind") sendDrawnContentBehind(page);
  }
  const bytes = await pdf.save();
  return pdfBytesToBlob(bytes);
}

/**
 * Parses a comma-separated list of page ranges like "1,3-5,8" against
 * a real page count, reusing parseSplitRange for each comma-separated
 * segment. Extracted from ExtractPdf.tsx, which originally parsed this
 * inline without any error handling (a non-numeric or out-of-range
 * segment would silently produce NaN rather than a clear message) --
 * this version surfaces the first invalid segment as a real error
 * instead.
 */
export function parseMultiRange(input: string, pageCount: number): SplitRangeResult {
  // Commas and semicolons both separate ranges ("1-3;4-6").
  const segments = input.split(/[,;]/).map(s => s.trim()).filter(Boolean);
  if (segments.length === 0) return { error: "Enter at least one page or range" };

  const allPages: number[] = [];
  for (const segment of segments) {
    const result = parseSplitRange(segment, pageCount);
    if ("error" in result) return result;
    allPages.push(...result.pages);
  }
  return { pages: allPages };
}

/**
 * Builds a new PDF containing exactly the given 0-indexed pages, in the
 * order given, extracted from ExtractPdf.tsx. Pairs with
 * parseMultiRange for the common "extract these pages/ranges" flow.
 */
export async function extractPdfPages(file: File, pageIndices: number[]): Promise<Blob> {
  const pdf = await PDFDocument.load(await file.arrayBuffer());
  const newPdf = await PDFDocument.create();
  await copyPagesWithStructure(pdf, newPdf, pageIndices);
  stampOutputMetadata(newPdf, { title: pdf.getTitle(), author: pdf.getAuthor(), fallbackTitle: stemOfPdfName(file.name) });
  const bytes = await newPdf.save();
  return pdfBytesToBlob(bytes);
}

/**
 * Builds a new PDF containing the given 0-indexed pages in a specific
 * order (allowing duplicates or omissions), extracted from
 * OrganizePdf.tsx. Structurally identical to extractPdfPages -- kept as
 * a separate, named function since "reorder" and "extract a subset"
 * are different enough operations conceptually that callers benefit
 * from the distinct name, even though the underlying pdf-lib calls are
 * the same.
 */
export async function reorderPdfPages(file: File, pageIndices: number[]): Promise<Blob> {
  return extractPdfPages(file, pageIndices);
}

/** One entry in an organized-page-list output -- either a real page
 * copied from the source PDF (optionally rotated further from
 * whatever rotation it already had), or a genuinely blank page with no
 * source content at all. */
export type OrganizePageSpec =
  | { type: "source"; index: number; rotation?: number }
  | { type: "blank" };

/**
 * The full-featured version of reorderPdfPages -- supports everything
 * that function does (reorder, omit, duplicate a source page by
 * listing its index more than once) plus two things it couldn't:
 * inserting a genuinely blank page at any position, and rotating an
 * individual page by a further 90/180/270 degrees on top of whatever
 * rotation it already had. A blank page's size matches the first real
 * source page found in the whole list (or falls back to A4 if every
 * entry is somehow blank), so it looks like a normal page in the
 * document rather than a default/arbitrary size.
 */
export async function buildOrganizedPdf(file: File, pageSpecs: OrganizePageSpec[]): Promise<Blob> {
  const srcPdf = await PDFDocument.load(await file.arrayBuffer());
  const newPdf = await PDFDocument.create();

  // A blank page is the size of the page before it (or after it, at the start), so it fits in (OG-7).
  const sizeNear = (at: number) => {
    for (let k = at - 1; k >= 0; k--) { const sp = pageSpecs[k]; if (sp.type === "source") return srcPdf.getPage(sp.index).getSize(); }
    for (let k = at + 1; k < pageSpecs.length; k++) { const sp = pageSpecs[k]; if (sp.type === "source") return srcPdf.getPage(sp.index).getSize(); }
    return { width: 595.28, height: 841.89 }; // A4 in points
  };

  const firstCopy = new Map<number, ReturnType<typeof newPdf.addPage>>();
  const copies = new Map<number, number>();
  for (let at = 0; at < pageSpecs.length; at++) {
    const spec = pageSpecs[at];
    if (spec.type === "blank") {
      const { width, height } = sizeNear(at);
      newPdf.addPage([width, height]);
      continue;
    }
    const [copiedPage] = await newPdf.copyPages(srcPdf, [spec.index]);
    if (spec.rotation) {
      const srcPage = srcPdf.getPage(spec.index);
      copiedPage.setRotation(degrees(srcPage.getRotation().angle + spec.rotation));
    }
    const n = (copies.get(spec.index) ?? 0) + 1;
    copies.set(spec.index, n);
    if (n > 1) renameFieldsOnPage(newPdf, copiedPage, n);
    const added = newPdf.addPage(copiedPage);
    if (!firstCopy.has(spec.index)) firstCopy.set(spec.index, added);
  }

  // Bookmarks follow their pages (a removed page's bookmark is dropped); form fields stay fillable (OG-1).
  writeOutline(newPdf, mapOutlineSubset(readOutline(srcPdf), firstCopy as never));
  carryFormFields(srcPdf, newPdf);

  stampOutputMetadata(newPdf, { title: srcPdf.getTitle(), author: srcPdf.getAuthor(), fallbackTitle: stemOfPdfName(file.name) });
  const bytes = await newPdf.save();
  return pdfBytesToBlob(bytes);
}

export interface ProtectOptions {
  /** Things a reader is asked to block. The PDF standard leaves enforcement to the reader. */
  restrict?: { printing?: boolean; copying?: boolean; modifying?: boolean };
}

/**
 * Adds a password to a PDF with AES-256 (the cipher the PDF 2.0 standard recommends; passwords are
 * Unicode, so accented and non-Latin letters work in every reader). The document is loaded and saved
 * whole, so title, bookmarks, form fields and links stay as they were. With restrictions, a random
 * owner password is generated and never shown, as other tools do.
 */
export async function protectPdfWithPassword(file: File, password: string, options: ProtectOptions = {}): Promise<Blob> {
  const { PDFDocument: CantooPdf } = await import("@cantoo/pdf-lib");
  const doc = await CantooPdf.load(new Uint8Array(await file.arrayBuffer()), { updateMetadata: false });
  const r = options.restrict ?? {};
  const restricted = !!(r.printing || r.copying || r.modifying);
  let ownerPassword = password;
  if (restricted) {
    const rnd = crypto.getRandomValues(new Uint8Array(24));
    ownerPassword = Array.from(rnd, (b) => b.toString(16).padStart(2, "0")).join("");
  }
  doc.setProducer("FreeDailyPro");
  doc.encrypt({
    userPassword: password,
    ownerPassword,
    algorithm: "AES-256",
    permissions: restricted ? { printing: r.printing ? false : "highResolution", copying: !r.copying, modifying: !r.modifying, annotating: !r.modifying, fillingForms: !r.modifying, contentAccessibility: true, documentAssembly: !r.modifying } : undefined,
  } as never);
  const bytes = await doc.save();
  return new Blob([new Uint8Array(bytes)], { type: "application/pdf" });
}

export interface ProtectCheck {
  ok: boolean;
  pages: number;
  reason?: string;
}

/**
 * Opens the protected file with the typed password in pdf.js, the reader inside Firefox and the one
 * this site uses everywhere, and compares it with the original (PWD-6).
 */
export async function verifyProtectedPdf(original: File, protectedBlob: Blob, password: string): Promise<ProtectCheck> {
  try {
    const raw = new Uint8Array(await protectedBlob.arrayBuffer());
    const { PDFDocument: CantooPdf } = await import("@cantoo/pdf-lib");
    const locked = await CantooPdf.load(raw, { ignoreEncryption: true, updateMetadata: false });
    if (!locked.isEncrypted) return { ok: false, pages: 0, reason: "The file did not come out protected." };
    const snapshot = async (bytes: Uint8Array, pw?: string) => {
      const doc = await openPdfBytes(bytes, pw);
      const meta = (await doc.getMetadata().catch(() => null))?.info as { Title?: string } | undefined;
      const fields = Object.keys((await doc.getFieldObjects().catch(() => null)) ?? {}).sort().join("|");
      const out = { pages: doc.numPages, title: meta?.Title ?? "", fields };
      return out;
    };
    const before = await snapshot(new Uint8Array(await original.arrayBuffer()));
    const after = await snapshot(raw, password);
    if (after.pages !== before.pages) return { ok: false, pages: after.pages, reason: "The page count changed." };
    if (after.title !== before.title) return { ok: false, pages: after.pages, reason: "The title changed." };
    if (after.fields !== before.fields) return { ok: false, pages: after.pages, reason: "Some form fields were lost." };
    return { ok: true, pages: after.pages };
  } catch (err) {
    console.error("Protected PDF check failed:", err);
    return { ok: false, pages: 0, reason: "The protected file could not be opened with that password." };
  }
}

export interface RepairPdfResult {
  blob: Blob;
  pageCount: number;
  /** Pages the file says it had, when that could be read and is more than was recovered (a cut-off file). */
  totalPages: number | null;
  /** True when only the part of the file that survived could be rebuilt (the file ends part-way). */
  partial: boolean;
  /** What repair did, in one plain sentence: "Rebuilt the cross-reference table; recovered 2 pages". */
  summary: string;
}

/** Plain-language reasons a file could not be repaired. The technical detail goes to the console only. */
export class RepairError extends Error {
  constructor(message: string, public kind: "not-pdf" | "unrecoverable") { super(message); }
}

/**
 * Attempts to recover a damaged PDF. First a lenient normal load (fixes a wrong or missing index, junk before
 * the header, a missing end marker); if that finds no pages, a scan that reads every complete object and
 * rebuilds the page tree from the pages that survived (cut-off downloads, lost trailers).
 */
export async function repairPdf(file: File): Promise<RepairPdfResult> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const { sniffNotPdf, recoverPagesByScan } = await import("./pdfRecover");
  const { notPdfMessage, diagnoseStructure, repairSummary } = await import("./repairDiagnose");
  const notPdf = sniffNotPdf(bytes.subarray(0, 1024));
  if (notPdf) throw new RepairError(notPdfMessage(notPdf), "not-pdf");
  const diagnosis = diagnoseStructure(bytes); // looks at the original only; recovery below is unchanged

  let looksCut = false;
  try {
    const tail = new TextDecoder("latin1").decode(bytes.subarray(Math.max(0, bytes.length - 2048)));
    looksCut = !tail.includes("%%EOF");
  } catch { /* ignore */ }

  try {
    const pdf = await PDFDocument.load(bytes, { throwOnInvalidObject: false, ignoreEncryption: true, updateMetadata: false });
    const pageCount = pdf.getPageCount();
    if (pageCount > 0) {
      const repaired = await pdf.save();
      return { blob: pdfBytesToBlob(repaired), pageCount, totalPages: null, partial: false, summary: repairSummary({ diagnosis, method: "lenient", recovered: pageCount, total: null }) };
    }
  } catch (err) {
    console.warn("Lenient load failed, trying a scan:", err);
  }
  try {
    const r = await recoverPagesByScan(bytes);
    const partial = looksCut || (r.total !== null && r.total > r.recovered);
    return { blob: pdfBytesToBlob(r.bytes), pageCount: r.recovered, totalPages: r.total, partial, summary: repairSummary({ diagnosis, method: "scan", recovered: r.recovered, total: r.total }) };
  } catch (err) {
    console.warn("Scan recovery failed:", err);
    throw new RepairError("This file couldn't be recovered — no complete page could be found in it.", "unrecoverable");
  }
}

export type ComparePdfResult =
  | { kind: "diff"; diff: DiffLine[] }
  | { kind: "too-large" }
  | { kind: "no-text" };

export { MAX_DIFF_LINES, type DiffLine };

/**
 * Compares the real text content of two PDFs and returns a line-level
 * diff, extracted from ComparePdf.tsx. Reuses the same diff engine as
 * the Diff Checker tool. This compares actual wording, not a
 * visual/pixel comparison -- a font or color change with no text
 * change behind it won't be flagged. Returns "too-large" if either
 * PDF's text exceeds MAX_DIFF_LINES rather than attempting an
 * expensive diff that would hang the browser tab, and "no-text" if
 * neither PDF has any extractable text at all (both are likely scanned
 * images with no real text layer).
 */
export async function comparePdfText(fileA: File, fileB: File): Promise<ComparePdfResult> {
  const [pagesA, pagesB] = await Promise.all([extractPdfTextLines(fileA), extractPdfTextLines(fileB)]);
  const linesA = pagesA.flat();
  const linesB = pagesB.flat();

  if (linesA.length > MAX_DIFF_LINES || linesB.length > MAX_DIFF_LINES) {
    return { kind: "too-large" };
  }
  if (linesA.length === 0 && linesB.length === 0) {
    return { kind: "no-text" };
  }
  return { kind: "diff", diff: diffLines(linesA, linesB) };
}

export type ImagePageSizeId = "a4" | "letter" | "original";

export const IMAGE_PDF_PAGE_SIZES: Record<ImagePageSizeId, { width: number; height: number; label: string }> = {
  a4: { width: 595.28, height: 841.89, label: "A4" },
  letter: { width: 612, height: 792, label: "US Letter" },
  original: { width: 0, height: 0, label: "Original image size (no fitting)" },
};

export interface CustomPageSize {
  widthPt: number;
  heightPt: number;
}

/** A resolved page size: one of the named presets, "original" (each
 * page sized to its own image), or a custom width/height in points. */
export type ResolvedPageSize = ImagePageSizeId | CustomPageSize;


/**
 * Combines multiple images into one PDF, one image per page, extracted
 * from ImageToPdf.tsx. "original" page size uses each image's own
 * pixel dimensions directly with no fitting; a named preset or a
 * custom width/height centers and scales each image down (never up)
 * to fit within the page's margins, preserving aspect ratio.
 */
export async function convertImagesToPdf(
  files: File[],
  pageSize: ResolvedPageSize,
  onProgress?: (current: number, total: number) => void
): Promise<Blob> {
  const { buildImagePdf, DEFAULT_IMAGE_PDF_OPTIONS } = await import("./imagePdf");
  const out = await buildImagePdf(files, { ...DEFAULT_IMAGE_PDF_OPTIONS, pageSize, originalDpi: 72 }, { progress: onProgress });
  if (out.skipped.length) throw new Error(`${out.skipped[0].name} ${out.skipped[0].reason}`);
  return pdfBytesToBlob(out.bytes);
}

export type SplitDirection = "vertical" | "horizontal";


/**
 * Lays out images two-per-page (an odd image out at the end gets its
 * own full page, using the same fit-to-page logic as
 * convertImagesToPdf), replacing an earlier general N-image auto-grid
 * version -- scoped down deliberately so the vertical/horizontal split
 * choice stays meaningful. Callers are expected to have already
 * applied any per-image rotation (see rotateImageToFile) before
 * calling this -- rotation happens on the actual image pixels, not as
 * a PDF-level transform, so it composes cleanly with the fit-to-cell
 * math here without needing special-cased rotation anchor logic.
 */
export async function pairImagesOntoPages(files: File[], pageSize: ResolvedPageSize, direction: SplitDirection): Promise<Blob> {
  const { buildImagePdf, DEFAULT_IMAGE_PDF_OPTIONS } = await import("./imagePdf");
  const out = await buildImagePdf(files, { ...DEFAULT_IMAGE_PDF_OPTIONS, pageSize, originalDpi: 72, pair: direction });
  if (out.skipped.length) throw new Error(`${out.skipped[0].name} ${out.skipped[0].reason}`);
  return pdfBytesToBlob(out.bytes);
}

/**
 * Rotates an image's actual pixels by a multiple of 90 degrees, giving
 * back a new File with the same name/type. Used as a pre-processing
 * step before either convertImagesToPdf or pairImagesOntoPages -- doing
 * the rotation on the real pixel data (via canvas) rather than as a
 * PDF-level drawImage rotation keeps the fit-to-page/fit-to-cell math
 * in both of those functions simple: a rotated image's own width/height
 * already reflect the new orientation (90/270 swap them), so nothing
 * downstream needs to know rotation happened at all.
 */
export async function rotateImageToFile(file: File, degrees: 0 | 90 | 180 | 270): Promise<File> {
  if (degrees === 0) return file;
  const img = await loadImageElement(file);
  const swap = degrees === 90 || degrees === 270;
  const canvas = document.createElement("canvas");
  canvas.width = swap ? img.naturalHeight : img.naturalWidth;
  canvas.height = swap ? img.naturalWidth : img.naturalHeight;
  const ctx = canvas.getContext("2d")!;
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((degrees * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  const mime = file.type === "image/png" ? "image/png" : "image/jpeg";
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("Rotation failed"))), mime, 0.95);
  });
  return new File([blob], file.name, { type: mime });
}

/** Real HEIC/HEIF decode (via heic2any, the same package and pattern
 * already verified working in UniversalFileConverter.tsx) into a
 * normal JPEG File -- from there it's just a regular image, so this
 * hands off to the same pdf-lib embedJpg path every other JPEG
 * already goes through rather than needing its own separate handling
 * downstream. Exists specifically because Image to PDF's file picker
 * previously only accepted PNG/JPEG, silently rejecting the exact
 * format an iPhone camera produces by default. */
export async function convertHeicToJpegFile(file: File): Promise<File> {
  const isHeic = file.type === "image/heic" || file.type === "image/heif" || /\.(heic|heif)$/i.test(file.name);
  if (!isHeic) return file;
  const heic2any = (await import("heic2any")).default;
  const result = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  const blob = Array.isArray(result) ? result[0] : result;
  return new File([blob], file.name.replace(/\.(heic|heif)$/i, ".jpg"), { type: "image/jpeg" });
}

/** WEBP decode into a PNG File via canvas -- pdf-lib itself has no
 * embedWebp method (only embedPng/embedJpg), but every modern browser
 * can already decode a WEBP image into an <img> element natively, so
 * this just redraws it onto a canvas and re-exports as PNG rather than
 * needing a separate WEBP-decoding library. */
export async function convertWebpToPngFile(file: File): Promise<File> {
  const isWebp = file.type === "image/webp" || /\.webp$/i.test(file.name);
  if (!isWebp) return file;
  const img = await loadImageElement(file);
  const canvas = document.createElement("canvas");
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  canvas.getContext("2d")!.drawImage(img, 0, 0);
  const blob: Blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("WEBP conversion failed"))), "image/png");
  });
  return new File([blob], file.name.replace(/\.webp$/i, ".png"), { type: "image/png" });
}

/**
 * Extracts real paragraph text from a .docx file, extracted from
 * WordToPdf.tsx. A .docx is a ZIP archive of XML, not plain text, so
 * file.text() on it just produces garbage bytes -- this reads the
 * actual document.xml and pulls out real paragraph text. Same approach
 * already verified working for the Paper Format Checker tool.
 */
export async function extractDocxText(file: File): Promise<string> {
  const { readDocx } = await import("./docxRead");
  const { blocksText } = await import("./docxModel");
  const doc = await readDocx(file);
  return doc.sections.map((sec) => blocksText(sec.blocks)).join("\n");
}

/**
 * Lays out plain text as a simple paginated PDF, extracted from
 * WordToPdf.tsx. Real word-wrapping (measuring each word's actual
 * rendered width and breaking to a new line before exceeding the page's
 * text width) rather than the previous behavior of silently truncating
 * any line past a fixed character count -- that was real, silent data
 * loss for a "convert my document" tool (anything past ~90 characters
 * on a line just vanished from the output with no indication), not a
 * reasonable simplification worth keeping. Still strips characters the
 * standard Helvetica font can't encode (WinAnsi only -- no CJK,
 * emoji, or characters beyond Latin-1) rather than letting pdf-lib
 * throw on the whole document over one bad character; this is a real,
 * honest limitation of using a standard, non-embedded font, not
 * something word-wrapping fixes.
 */
/** A .docx as a PDF with its formatting (the engine behind Word to PDF), for callers that only need the file. */
export async function convertDocxToPdfBlob(file: File): Promise<Blob> {
  const [{ readDocx }, { convertDocxDocumentToPdf }] = await Promise.all([import("./docxRead"), import("./docxToPdf")]);
  const doc = await readDocx(file);
  const out = await convertDocxDocumentToPdf(doc, { fallbackTitle: file.name.replace(/\.[^.]+$/, "") });
  return pdfBytesToBlob(out.bytes);
}

export async function convertTextToPdf(text: string, title?: string): Promise<Blob> {
  const { convertTextToPdfDocument } = await import("./textToPdf");
  const out = await convertTextToPdfDocument(text, { title });
  return pdfBytesToBlob(out.bytes);
}

export interface FlattenPdfResult {
  blob: Blob;
  fieldCount: number;
}

/**
 * Permanently bakes a PDF's fillable form field values into the page
 * content, so they can no longer be edited as fields -- the general,
 * standalone version of the flatten step that used to live only inside
 * FillableForms.tsx. Genuinely useful beyond that one tool: any PDF
 * with real AcroForm fields someone has already filled in (in this
 * site's own tools, in Acrobat, in a browser's native PDF viewer,
 * anywhere) can be locked down here before being sent on, so the
 * person receiving the final copy can't accidentally or intentionally
 * change what was filled in. Throws if the PDF has no form fields at
 * all, since flattening nothing isn't a real, useful action -- the
 * caller's error handling can point the person at why.
 *
 * Stamps a small, permanent timestamp on every page recording when
 * flattening happened. This doesn't prove WHO flattened the file --
 * there's no server here to verify identity, so that's a genuine limit
 * of what a fully client-side tool can offer -- but it does give
 * anyone reviewing the document later a real, verifiable record to
 * sanity-check against (a file flattened suspiciously soon after being
 * sent, before the intended signer plausibly could have reviewed it,
 * is now something a reader can actually notice).
 */
/** How many fillable fields a PDF has, and how many of them are signature fields (flattening those breaks a digital signature). */
export async function inspectFormFields(file: File): Promise<{ fieldCount: number; signatureCount: number }> {
  const pdfDoc = await PDFDocument.load(new Uint8Array(await file.arrayBuffer()), { ignoreEncryption: true });
  const fields = pdfDoc.getForm().getFields();
  return { fieldCount: fields.length, signatureCount: fields.filter((f) => f instanceof PDFSignature).length };
}

export async function flattenPdf(file: File, options: { stamp?: boolean; stampZone?: StampZone } = {}): Promise<FlattenPdfResult> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  const pdfDoc = await PDFDocument.load(bytes);
  const form = pdfDoc.getForm();
  const fieldCount = form.getFields().length;
  if (fieldCount === 0) {
    throw new Error("This PDF doesn't have any fillable form fields to flatten — it may already be flattened, or it never had real fillable fields to begin with.");
  }
  // Redraw answers whose stored drawing is stale (forms saved with "NeedAppearances"), in any script.
  const refreshFailures = await refreshAppearances(pdfDoc);
  if (refreshFailures.length > 0) {
    throw new Error(`Couldn't draw the answer in ${refreshFailures.slice(0, 3).map((f) => `"${f.name}"`).join(", ")} — nothing was flattened, so no entry is lost.`);
  }
  appearancesAreFinal(pdfDoc);
  form.flatten({ updateFieldAppearances: false });
  dropDeadAnnotations(pdfDoc);

  if (options.stamp) {
    const helvetica = await pdfDoc.embedFont(StandardFonts.Helvetica);
    const stampText = flattenStampText(new Date(), options.stampZone ?? "local");
    for (const page of pdfDoc.getPages()) {
      const view = pageView(page);
      const at = view.toStored(20, 8);
      page.drawText(stampText, {
        x: at.x, y: at.y, size: 6.5, font: helvetica, color: rgb(0.6, 0.6, 0.6), maxWidth: view.width - 40, rotate: degrees(view.rotation),
      });
    }
  }

  pdfDoc.setProducer("FreeDailyPro");
  pdfDoc.setModificationDate(new Date());
  const flattenedBytes = await pdfDoc.save({ updateFieldAppearances: false });
  return { blob: pdfBytesToBlob(flattenedBytes), fieldCount };
}

export interface PdfToJpgOptions {
  /** Resolution multiplier (1 = 72 dpi): higher = sharper but larger. */
  scale?: number;
  format?: "image/jpeg" | "image/png";
  /** 0-1, ignored for PNG since it's lossless. */
  quality?: number;
  /** 1-indexed pages, e.g. "1, 3-5" or "4-". Single-file conversions only: with several files one
   *  range has no single meaning, so it is ignored. */
  pageRange?: string;
  /** Polled between pages; returning true stops the run (throws PdfToJpgCancelled). */
  isCancelled?: () => boolean;
}

export interface PdfToJpgImage {
  /** File name inside the ZIP (without the folder). */
  name: string;
  /** Which source file (index into the loaded files) and page it came from. */
  sourceName: string;
  pageNum: number;
  width: number;
  height: number;
  blob: Blob;
}

export interface PdfToJpgResult {
  blob: Blob;
  filename: string;
  /** Number of images produced and number of PDFs they came from. */
  imageCount: number;
  fileCount: number;
  /** PDFs that could not be read (damaged, password-protected...), with the reason. */
  skipped: { name: string; reason: string }[];
  /** The first images (capped, they are already in memory) for the on-screen preview. */
  previews: PdfToJpgImage[];
  /** Pages whose resolution was lowered to fit the browser's maximum image size. */
  reducedPages: number;
}

export class PdfToJpgCancelled extends Error {
  constructor() {
    super("Conversion stopped");
    this.name = "PdfToJpgCancelled";
  }
}

/** Largest picture (in pixels) browsers reliably draw: iOS Safari stops at about 16.7 million. */
const MAX_IMAGE_PIXELS = 16_000_000;
const MAX_PREVIEWS = 12;

/**
 * Converts the pages of one or more PDFs into JPG/PNG images.
 *  - Exactly one image in the end → that image alone ("name-page-03.jpg"), however it was selected.
 *  - Otherwise a ZIP; with several PDFs each gets its own folder (same-named PDFs get " (2)", " (3)").
 *  - A PDF that cannot be read is skipped and reported instead of failing the whole batch.
 */
export async function convertPdfsToJpgs(
  files: File[],
  onProgress?: (done: number, total: number) => void,
  options: PdfToJpgOptions = {}
): Promise<PdfToJpgResult> {
  const { scale = 2, format = "image/jpeg", quality = 0.92, pageRange, isCancelled } = options;
  const ext = format === "image/png" ? "png" : "jpg";
  const check = () => {
    if (isCancelled?.()) throw new PdfToJpgCancelled();
  };

  type Loaded = { file: File; pdf: Awaited<ReturnType<typeof loadPdfDocument>>; pages: number[] };
  const loaded: Loaded[] = [];
  const skipped: { name: string; reason: string }[] = [];
  for (const file of files) {
    check();
    let pdf: Awaited<ReturnType<typeof loadPdfDocument>>;
    try {
      pdf = await loadPdfDocument(file);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "";
      skipped.push({ name: file.name, reason: /password/i.test(msg) ? "password-protected" : "damaged or not a real PDF" });
      continue;
    }
    let pages = Array.from({ length: pdf.numPages }, (_, i) => i);
    if (pageRange && files.length === 1) {
      // A bad range is the person's input, not a damaged file: stop with its message.
      const parsed = parseMultiRange(pageRange, pdf.numPages);
      if ("error" in parsed) throw new Error(parsed.error);
      pages = uniquePages(parsed.pages);
    }
    loaded.push({ file, pdf, pages });
  }
  if (loaded.length === 0) {
    throw new Error(files.length === 1 ? "This PDF could not be read. It may be damaged or password-protected." : "None of these PDFs could be read. They may be damaged or password-protected.");
  }

  const totalPages = loaded.reduce((n, l) => n + l.pages.length, 0);
  const folders = uniqueFolderNames(loaded.map((l) => stemForImages(l.file.name)));
  const multi = loaded.length > 1;
  const zip = new JSZip();
  const images: PdfToJpgImage[] = [];
  let done = 0;
  let reducedPages = 0;

  for (let fi = 0; fi < loaded.length; fi++) {
    const { file, pdf, pages } = loaded[fi];
    const fileImages: PdfToJpgImage[] = [];
    const startDone = done;
    let reducedHere = 0;
    try {
      for (const idx of pages) {
        check();
        onProgress?.(done, totalPages);
        const page = await pdf.getPage(idx + 1);
        const base = page.getViewport({ scale: 1 });
        const px = base.width * base.height * scale * scale;
        const pageScale = px > MAX_IMAGE_PIXELS ? Math.sqrt(MAX_IMAGE_PIXELS / (base.width * base.height)) : scale;
        if (pageScale !== scale) reducedHere++;
        const blob = await renderPageToImageBlob(page, pageScale, format, quality);
        check();
        const name = imageFileName(stemForImages(file.name), idx + 1, pdf.numPages, ext);
        fileImages.push({ name, sourceName: file.name, pageNum: idx + 1, width: Math.floor(page.getViewport({ scale: pageScale }).width), height: Math.floor(page.getViewport({ scale: pageScale }).height), blob }); // a canvas truncates, so the caption does too
        done++;
      }
    } catch (err) {
      if (err instanceof PdfToJpgCancelled) throw err;
      // One PDF whose pages cannot be drawn must not cost the person every other file.
      skipped.push({ name: file.name, reason: "its pages could not be drawn" });
      done = startDone + pages.length; // keep the progress bar honest
      continue;
    }
    reducedPages += reducedHere;
    for (const img of fileImages) zip.file(multi ? `${folders[fi]}/${img.name}` : img.name, img.blob);
    images.push(...fileImages);
  }
  if (images.length === 0) {
    throw new Error(files.length === 1 ? "This PDF could not be converted. It may be damaged." : "None of these PDFs could be converted. They may be damaged.");
  }
  onProgress?.(totalPages, totalPages);
  const previews = images.slice(0, MAX_PREVIEWS);
  const common = { imageCount: images.length, fileCount: loaded.length, skipped, previews, reducedPages };

  if (images.length === 1) {
    return { blob: images[0].blob, filename: images[0].name, ...common };
  }
  const zipBlob = await zip.generateAsync({ type: "blob" });
  const zipName = multi
    ? `converted-images-${loaded.length}-files.zip`
    : `${stemForImages(loaded[0].file.name)}-images.zip`;
  return { blob: zipBlob, filename: zipName, ...common };
}

/**
 * Converts an Excel workbook (.xlsx) into a PDF with real cell data
 * laid out as an actual readable table, extracted from ExcelToPdf.tsx.
 * Each sheet becomes its own section in landscape orientation, with
 * column headers repeated at the top whenever a table runs onto a new
 * page. Reads real cell values -- doesn't reproduce colors, fonts,
 * conditional formatting, or embedded charts. Throws if the workbook
 * has no sheets, or every sheet is empty, since there'd be nothing
 * real to put in the output either way.
 */
export async function convertExcelToPdf(file: File): Promise<Blob> {
  const { loadWorkbook, convertWorkbookToPdf, DEFAULT_EXCEL_PDF_OPTIONS } = await import("./excelPdf");
  const wb = await loadWorkbook(file);
  const out = await convertWorkbookToPdf(wb, DEFAULT_EXCEL_PDF_OPTIONS, file.name.replace(/\.[^.]+$/, ""));
  return pdfBytesToBlob(out.bytes);
}

import { sanitizeForWinAnsi, sanitizeForWinAnsiSingleLine, canWinAnsi } from "./pdfFonts";
export { sanitizeForWinAnsi, sanitizeForWinAnsiSingleLine };

/**
 * Converts a PowerPoint presentation (.pptx) into a PDF by rasterizing
 * each real slide -- see pptxRasterize.ts for the full implementation
 * and why this replaced an earlier text-extraction approach that
 * dropped roughly half of any real deck's content (backgrounds, cards,
 * images, theme colors). Kept as a thin wrapper here, rather than
 * moving every call site over to the new module directly, so the two
 * other existing callers (UniversalFileConverter.tsx,
 * expandedOperations.ts) keep working unchanged at the same default
 * quality. PowerPointToPdf.tsx itself calls convertPowerPointToPdfRaster
 * directly instead, since it needs the progress callback and quality/
 * hidden-slide options this wrapper doesn't expose.
 */
export async function convertPowerPointToPdf(file: File): Promise<Blob> {
  const { convertPowerPointToPdfRaster } = await import("./pptxRasterize");
  return convertPowerPointToPdfRaster(file, { quality: "default", includeHidden: false });
}

export interface PdfToExcelResult {
  blob: Blob;
  /** True when at least one table was found and written. */
  hasData: boolean;
}

/**
 * Tables from a PDF as an Excel workbook (numbers and dates as real values, continued tables
 * joined, prose left out). This is the plain, no-OCR entry point used by the Universal converter;
 * the PDF to Excel tool itself uses convertPdfToExcelDocument, which adds options, preview and OCR.
 */
export async function convertPdfToExcel(file: File): Promise<PdfToExcelResult> {
  const { convertPdfToExcelDocument, DEFAULT_RUN_OPTIONS } = await import("./pdfToExcel");
  const pdf = await loadPdfDocument(file);
  const out = await convertPdfToExcelDocument(pdf, { ...DEFAULT_RUN_OPTIONS, ocr: false }, {
    isCurrent: () => true,
    status: () => {},
    progress: () => {},
    registerWorker: () => {},
  });
  return out.kind === "ok" ? { blob: out.blob, hasData: true } : { blob: new Blob(), hasData: false };
}

export interface OcrPdfResult {
  blob: Blob;
  pagesOcrd: number;
  /** Words recognised on each page that was read (0-based page index → count). */
  wordsByPage: Record<number, number>;
  /** Scanned pages that were sent to OCR (whether or not anything was recognised). */
  pagesAttempted: number;
  /** 1-based pages whose reading was noise (often a sideways scan); no text layer was added to them. */
  unreadablePages: number[];
  /** 1-based pages that were turned (clockwise degrees) so the text reads upright. */
  rotatedPages: { page: number; degrees: number }[];
}

export type OcrPageRotation = 0 | 90 | 180 | 270 | "auto";

/**
 * Makes a scanned PDF's text searchable/selectable while keeping it
 * visually identical, extracted from OcrPdf.tsx. Each page is checked
 * individually -- pages with real, existing text pass through
 * unchanged (just copied over), and only genuinely image-only pages
 * get OCR applied, with the recognized words placed as an invisible
 * text layer positioned to match their real location on the page (the
 * same technique tools like Adobe Acrobat's "Recognize Text" use).
 */
export async function ocrPdf(
  file: File,
  onProgress?: (message: string, percent?: number) => void,
  options: { language?: string; isCurrent?: () => boolean; rotate?: OcrPageRotation } = {}
): Promise<OcrPdfResult> {
  const language = options.language ?? "eng";
  const alive = () => {
    if (options.isCurrent && !options.isCurrent()) throw new Error("cancelled");
  };
  const srcBytes = new Uint8Array(await file.arrayBuffer());
  const src = await PDFDocument.load(srcBytes, { updateMetadata: false });
  const out = await PDFDocument.create();
  const invisibleFont = await out.embedFont(StandardFonts.Helvetica);
  // Hindi (Devanagari) words need a font that has those letters, for the hidden text only: it makes
  // them searchable and copyable; the visible page is the untouched original.
  let devanagariFont: Awaited<ReturnType<typeof out.embedFont>> | null = null;
  const wantsDevanagari = allowsDevanagari(language);

  const pdfjsDoc = await loadPdfDocument(file);
  const textPages = await extractPdfTextLinesWithPosition(file);
  const numPages = pdfjsDoc.numPages;
  let ocrdCount = 0;
  let pagesAttempted = 0;
  const unreadablePages: number[] = [];
  const rotatedPages: { page: number; degrees: number }[] = [];
  const rotateOpt = options.rotate ?? 0;
  const wordsByPage: Record<number, number> = {};
  const { startOcrEngine } = await import("./ocrEngine");
  let engine: Awaited<ReturnType<typeof startOcrEngine>> | null = null;
  const scale = 3;

  try {
    for (let i = 0; i < numPages; i++) {
      alive();
      const pageNum = i + 1;
      // Requires a real, meaningful amount of text, not just "any line objects exist at all": a
      // single stray character surviving text extraction must not be enough to skip OCR on a page
      // that is clearly an image (found with a PDF made by Image to PDF).
      const pageText = (textPages[i] ?? []).map(l => l.text).join(" ").trim();
      const hasRealText = pageText.length >= 3;

      const [copiedPage] = await out.copyPages(src, [i]);
      const newPage = out.addPage(copiedPage);
      if (hasRealText) continue;

      if (!engine) onProgress?.("Starting OCR (first use downloads the language data)...", (i / numPages) * 100);
      onProgress?.(`Reading page ${pageNum} of ${numPages} with OCR...`, (i / numPages) * 100);
      const pdfjsPage = await pdfjsDoc.getPage(pageNum);
      const rawImageBlob = await renderPageToJpegBlob(pdfjsPage, scale, 0.92);
      alive();

      if (!engine) {
        engine = await startOcrEngine(language, (f) => onProgress?.(`Reading page ${pageNum} of ${numPages} with OCR... ${Math.round(f * 100)}%`, ((i + f) / numPages) * 100));
        alive();
      }
      pagesAttempted++;
      // A chosen turn is applied to the picture before reading; "auto" lets the engine try the other orientations.
      const manualTurn = rotateOpt === "auto" || rotateOpt === 0 ? 0 : rotateOpt;
      const imageToRead = manualTurn ? await rotateImageBlob(rawImageBlob, manualTurn) : rawImageBlob;
      alive();
      const data = await engine.read(imageToRead, { isCurrent: options.isCurrent, autoRotate: rotateOpt === "auto", status: (m) => onProgress?.(`Page ${pageNum}: ${m}`, (i / numPages) * 100) });
      alive();
      const turn = manualTurn || data.rotation || 0;

      // Noise (a sideways scan, a photo, handwriting): say so rather than hide a garbage text layer under the page.
      if (looksUnreadable({ text: data.text ?? "", confidence: data.confidence })) {
        unreadablePages.push(pageNum);
        continue;
      }
      if (turn) {
        // The page is turned so the picture and the text on it are upright; the text layer then lines up with it.
        newPage.setRotation(degrees((((newPage.getRotation().angle + turn) % 360) + 360) % 360));
        rotatedPages.push({ page: pageNum, degrees: turn });
      }

      const ocrView = pageView(newPage);
      const { width: pageWidthPt, height: pageHeightPt } = ocrView; // the page as displayed, which is what was read
      // Words are placed by the pixel size of the (possibly turned) render; map that to the displayed page's size.
      const vp = pdfjsPage.getViewport({ scale });
      const sx = pageWidthPt / (turn % 180 === 90 ? vp.height : vp.width);
      const sy = pageHeightPt / (turn % 180 === 90 ? vp.width : vp.height);
      let wordsPlaced = 0;
      for (const block of data.blocks ?? []) {
        for (const paragraph of block.paragraphs ?? []) {
          for (const line of paragraph.lines ?? []) {
            const ln = line as unknown as { text?: string; bbox?: { x0: number; y0: number; x1: number; y1: number }; words?: { text: string }[] };
            let text = (ln.text ?? (ln.words ?? []).map((w) => w.text).join(" ")).replace(/\s+/g, " ").trim();
            if (!text || !ln.bbox) continue;
            // One text run per line, so copying gives lines (not one word per row) and a phrase can be found.
            let font = invisibleFont;
            if (wantsDevanagari && /[\u0900-\u097f]/.test(text)) {
              if (!devanagariFont) {
                out.registerFontkit(fontkit);
                const res = await fetch("/fonts/FreeSans-devanagari-subset.ttf");
                if (res.ok) devanagariFont = await out.embedFont(new Uint8Array(await res.arrayBuffer()), { subset: false });
              }
              if (devanagariFont) {
                font = devanagariFont;
                const set = new Set(devanagariFont.getCharacterSet());
                text = Array.from(text).filter((ch) => set.has(ch.codePointAt(0)!)).join("");
              }
            }
            if (font === invisibleFont) text = Array.from(text).filter((ch) => canWinAnsi(ch.codePointAt(0)!)).join("");
            if (!text.trim()) continue;
            const widthPt = (ln.bbox.x1 - ln.bbox.x0) * sx;
            const heightPt = (ln.bbox.y1 - ln.bbox.y0) * sy;
            const measured = font.widthOfTextAtSize(text, 1);
            if (measured <= 0 || widthPt <= 0) continue;
            // Wide enough to cover the visible line (a selection then lines up), within a sane range of its height.
            const size = Math.min(Math.max(widthPt / measured, heightPt * 0.5), heightPt * 1.4);
            const at = ocrView.toStored(ln.bbox.x0 * sx, pageHeightPt - ln.bbox.y1 * sy + heightPt * 0.2);
            newPage.drawText(text, { x: at.x, y: at.y, size, font, opacity: 0, rotate: degrees(ocrView.rotation) });
            wordsPlaced += text.split(" ").length;
          }
        }
      }
      if (wordsPlaced > 0) { ocrdCount++; wordsByPage[i] = wordsPlaced; }
    }
  } finally {
    if (engine) await engine.terminate();
  }

  stampOutputMetadata(out, { title: src.getTitle(), author: src.getAuthor(), fallbackTitle: file.name.replace(/\.pdf$/i, "") });
  const bytes = await out.save();
  return { blob: pdfBytesToBlob(bytes), pagesOcrd: ocrdCount, wordsByPage, pagesAttempted, unreadablePages, rotatedPages };
}



export { parsePdfFormFields, fillPdfForm, type FillableFieldEntry, type ParsedFillableForm, type FillResult } from "./formFill";

export type { RedactionBox } from "./redactSearch";
export { findTextInPdf } from "./redactSearch";
